import { useCallback, useRef, useState, type CSSProperties } from 'react'

import classes from './GivingActionConfetti.module.scss'

const DOT_COUNT = 8
const DOT_CLASSES = [classes.DotPrimary, classes.DotSuccess, classes.DotWarning, classes.DotAlert, classes.DotInfo]
const MIN_DISTANCE_PX = 28
const DISTANCE_SPREAD_PX = 32
const ANGLE_JITTER = 0.4

type Dot = Readonly<{
  id: string
  runId: number
  className: string
  left: number
  top: number
  end: string
}>

const createDots = (originX: number, originY: number, runId: number): Dot[] =>
  Array.from({ length: DOT_COUNT }, (_, index) => {
    const angle = (Math.PI * 2 * index) / DOT_COUNT + (Math.random() - 0.5) * ANGLE_JITTER
    const distance = MIN_DISTANCE_PX + Math.random() * DISTANCE_SPREAD_PX

    return {
      id: `${runId}-${index}`,
      runId,
      className: DOT_CLASSES[index % DOT_CLASSES.length],
      left: originX,
      top: originY,
      end: `translate(${Math.cos(angle) * distance}px, ${Math.sin(angle) * distance}px)`
    }
  })

export function useGivingActionConfetti() {
  const runIdRef = useRef(0)
  const [dots, setDots] = useState<Dot[]>([])

  const fire = useCallback((rowId: string) => {
    const row = document.querySelector(`[data-giving-action-row-id="${rowId}"]`)

    if (!row) {
      return
    }

    const originRect = (row.querySelector('button') ?? row).getBoundingClientRect()

    runIdRef.current += 1
    setDots(
      createDots(originRect.left + originRect.width / 2, originRect.top + originRect.height / 2, runIdRef.current)
    )
  }, [])

  const clearRun = useCallback((runId: number) => setDots(current => (current[0]?.runId === runId ? [] : current)), [])

  const confetti =
    dots.length > 0 ? (
      <div className={classes.Confetti} data-testid="giving-action-confetti">
        {dots.map(dot => (
          <span
            key={dot.id}
            className={dot.className}
            style={{ left: dot.left, top: dot.top, '--dot-end': dot.end } as CSSProperties}
            onAnimationEnd={() => clearRun(dot.runId)}
          />
        ))}
      </div>
    ) : null

  return { fire, confetti }
}
